<template>
  <div style="display: contents">
    <tr :class="{ 'table-warning': isDirty }">
      <td :style="{ 'padding-left': (level * 20 + 8) + 'px' }">
        <template v-if="hasChildren">
          <span @click="expanded = !expanded" class="cursor-pointer mr-2">
            <fa :icon="expanded ? 'minus' : 'plus'" />
          </span>
        </template>
        {{ product.name || product.product_master_id }}
        <small v-if="product.name && product.product_master_id" class="text-muted ml-1">{{ product.product_master_id }}</small>
        <b-badge v-if="product.status && product.status !== 'active'" variant="secondary" class="ml-1">
          {{ product.status }}
        </b-badge>
        <fa
          icon="pencil-alt"
          @click="openProductDetail"
          class="cursor-pointer ml-2 text-primary"
          style="font-size: 0.9em;"
        />
      </td>
      <td>
        <b-badge
          v-for="salesLine in rowSalesLines"
          :key="salesLine.id"
          variant="light"
          class="border mr-1 mb-1"
        >
          {{ salesLine.sales_line }}
        </b-badge>
      </td>
      <td>
        <b-form-select
          size="sm"
          :value="local.delivery"
          :options="deliveryOptions"
          @change="updateField('delivery', $event)"
        />
      </td>
      <td>
        <b-form-select
          size="sm"
          :value="local.delivery_mode"
          :options="deliveryModeOptions"
          @change="updateField('delivery_mode', $event)"
        />
      </td>
      <td class="text-center align-middle">
        <b-form-checkbox
          :checked="local.needs_verification"
          @change="updateField('needs_verification', $event)"
        />
      </td>
      <td>
        <b-form-select
          size="sm"
          :value="local.verification_requirement"
          :options="verificationRequirementOptions"
          @change="updateField('verification_requirement', $event)"
        />
      </td>
      <td>
        <b-form-input
          size="sm"
          type="number"
          step="1"
          :value="local.sort_order"
          @input="updateField('sort_order', toNullableInt($event))"
        />
      </td>
    </tr>
    <template v-if="expanded && hasChildren">
      <product-master-matrix-row
        v-for="child in product.children"
        :key="child.id"
        :product="child"
        :sales-lines="salesLines"
        :level="level + 1"
        @update="forwardUpdate"
      />
    </template>
  </div>
</template>

<script>
const EDITABLE_FIELDS = [
  'delivery',
  'delivery_mode',
  'needs_verification',
  'verification_requirement',
  'sort_order'
]

export default {
  name: 'ProductMasterMatrixRow',
  props: {
    product: {
      type: Object,
      required: true
    },
    salesLines: {
      type: Array,
      required: true
    },
    level: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      expanded: true,
      local: this.snapshot(this.product),
      deliveryOptions: [
        { value: null, text: '-' },
        { value: 'yearly', text: this.$t('sandoz.yearly') },
        { value: 'quarter', text: this.$t('sandoz.quarter') }
      ],
      deliveryModeOptions: [
        { value: null, text: '-' },
        { value: 'always', text: this.$t('sandoz.always') },
        { value: 'never', text: this.$t('sandoz.never') },
        { value: 'once_per_contract', text: this.$t('sandoz.once_per_contract') }
      ],
      verificationRequirementOptions: [
        { value: null, text: '-' },
        { value: 'with_every_delivery', text: this.$t('sandoz.with_every_delivery') },
        { value: 'once_a_year', text: this.$t('sandoz.once_a_year') }
      ]
    }
  },
  computed: {
    hasChildren() {
      return this.product.children && this.product.children.length > 0
    },
    rowSalesLines() {
      const ids = new Set(this.product.sales_line_ids || [])
      return this.salesLines.filter(sl => ids.has(sl.id))
    },
    changedFields() {
      const original = this.snapshot(this.product)
      const changed = {}
      EDITABLE_FIELDS.forEach(field => {
        if (!this.sameValue(original[field], this.local[field])) {
          changed[field] = this.local[field]
        }
      })
      return changed
    },
    isDirty() {
      return Object.keys(this.changedFields).length > 0
    }
  },
  watch: {
    product: {
      deep: true,
      handler(newVal) {
        // A reload after saving hands us fresh data: drop any local edits.
        this.local = this.snapshot(newVal)
      }
    }
  },
  methods: {
    snapshot(product) {
      return {
        delivery: product.delivery || null,
        delivery_mode: product.delivery_mode || null,
        needs_verification: !!product.needs_verification,
        verification_requirement: product.verification_requirement || null,
        sort_order: this.toNullableInt(product.sort_order)
      }
    },
    toNullableInt(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }
      const parsed = parseInt(value, 10)
      return Number.isNaN(parsed) ? null : parsed
    },
    sameValue(a, b) {
      return (a === null || a === undefined ? null : a) === (b === null || b === undefined ? null : b)
    },
    updateField(field, value) {
      this.local = { ...this.local, [field]: value }
      this.$emit('update', this.product.id, this.changedFields)
    },
    forwardUpdate(productMasterId, data) {
      this.$emit('update', productMasterId, data)
    },
    openProductDetail() {
      const route = this.$router.resolve({ path: `/sandoz/product_masters/${this.product.id}` })
      window.open(route.href, '_blank')
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
